// src/admin/lib/audit.ts — write an admin_audit_log row.
// Required on every admin mutation AND every signed-URL view of KYC media.
//
// Schema note: the admin_audit_log table (migration 20260418000002_create_admin.sql)
// uses column names `target_table` and `diff_json`. This helper exposes the
// friendlier `target_type` / `payload` names in its API and maps them on insert.

import { supabase } from './supabase';
import type { Json } from '@/lib/database.types';

export type AuditAction =
  | 'loan.approve' | 'loan.cancel' | 'loan.mark_disbursed' | 'loan.record_payment'
  | 'loan.request_restructure' | 'loan.recommend_default' | 'loan.force_fail_disbursement'
  | 'loan.reopen_cancelled' | 'loan.assign_collections'
  | 'borrower.freeze' | 'borrower.unfreeze' | 'borrower.reset_phone' | 'borrower.add_note'
  | 'kyc.approve' | 'kyc.reject' | 'kyc.request_resubmit' | 'kyc.view_media'
  | 'collection.log_contact'
  | 'admin.invite' | 'admin.deactivate' | 'admin.reset_totp' | 'admin.set_readonly' | 'admin.change_role'
  | 'config.update'
  | 'action.review'
  | 'search.global' | 'export.csv';

export type AuditTargetType =
  | 'loan' | 'borrower' | 'kyc' | 'admin' | 'payment' | 'config' | 'action_request' | 'storage';

export async function recordAudit(params: {
  action: AuditAction;
  target_type: AuditTargetType;
  target_id: string | null;
  payload?: Record<string, unknown>;
}): Promise<void> {
  const { error } = await supabase.from('admin_audit_log').insert({
    action: params.action,
    target_table: params.target_type,
    target_id: params.target_id,
    // The payload is caller-provided Record<string, unknown>; Json is the stricter
    // recursive union. We trust callers to pass serialisable data and cast here.
    diff_json: (params.payload ?? null) as Json,
  });
  if (error) {
    // Audit failure is logged but never blocks the action — downstream action still ran.
    // Sentry captures it; operator alerts on audit gaps via reconciliation.
    // eslint-disable-next-line no-console
    console.error('[audit] failed to record', params.action, error);
  }
}
